import { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { TestSchedulingStep } from '@/components/tests/TestSchedulingStep';
import { testApi } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { getErrorInfo } from '@/lib/errorUtils';
import TestCreationProgress from '@/components/tests/TestCreationProgress';

const TestSchedulePage: React.FC = () => {
  const { testId } = useParams<{ testId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const [loading, setLoading] = useState(false);

  // Get state from navigation
  const testTitle = location.state?.testTitle || 'Test';
  const mode = location.state?.mode || 'create';

  const handleBack = () => {
    // Navigate back to question preview
    navigate(`/tests/preview/${testId}`, {
      state: {
        testTitle: testTitle,
        mode: mode
      }
    });
  };

  const handleScheduleAndCreate = async (scheduleData: any) => {
    if (!testId) {
      console.error('TestSchedulePage: No testId provided');
      return;
    }

    console.log('TestSchedulePage: handleScheduleAndCreate called with:', scheduleData);
    console.log('TestSchedulePage: testId:', testId);
    console.log('TestSchedulePage: scheduleData.scheduleType:', scheduleData.scheduleType);
    console.log('TestSchedulePage: scheduleData.startTime:', scheduleData.startTime);
    console.log('TestSchedulePage: scheduleData.isOnDemandTest:', scheduleData.isOnDemandTest);

    if (scheduleData.scheduleType === 'scheduled' || scheduleData.scheduleType === 'on-demand') {
      // Actually schedule the test in the backend now (Step 3)
      console.log('TestSchedulePage: Scheduling test in backend...');
      try {
        setLoading(true);

        // Schedule the test in the backend
        await testApi.scheduleTest(testId, {
          startTime: scheduleData.startTime,
          endTime: scheduleData.endTime,
          isOnDemandTest: scheduleData.isOnDemandTest,
          onDemandTestStartDate: scheduleData.onDemandTestStartDate,
          onDemandTestEndDate: scheduleData.onDemandTestEndDate
        });

        toast({
          title: 'Success',
          description: 'Test scheduled successfully!',
        });

        // Navigate to assignment page for assignment step
        console.log('TestSchedulePage: Navigating to assignment page...');
        navigate(`/tests/assign/${testId}`, {
          state: {
            testTitle: testTitle,
            scheduleData: scheduleData,
            mode: mode
          }
        });
      } catch (error) {
        console.error('Failed to schedule test:', error);
        const errorInfo = getErrorInfo(error);
        toast({
          title: 'Error',
          description: `Failed to schedule test: ${errorInfo.message}`,
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    } else {
      // For draft tests, finalize directly
      try {
        setLoading(true);
        await testApi.finalizeTest(testId);

        toast({
          title: 'Success',
          description: 'Test created as draft successfully!',
        });

        navigate('/tests');
      } catch (error) {
        console.error('Failed to create test:', error);
        const errorInfo = getErrorInfo(error);
        toast({
          title: 'Error',
          description: `Failed to create test: ${errorInfo.message}`,
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCreateWithoutScheduling = async () => {
    if (!testId) return;

    try {
      setLoading(true);

      // Just finalize the test as draft
      await testApi.finalizeTest(testId);

      toast({
        title: 'Success',
        description: 'Test created as draft successfully!',
      });

      navigate('/tests');
    } catch (error) {
      console.error('Failed to create test:', error);
      const errorInfo = getErrorInfo(error);
      toast({
        title: 'Error',
        description: `Failed to create test: ${errorInfo.message}`,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Progress Indicator - Only show during creation flow */}
      {mode === 'create' && (
        <TestCreationProgress currentStep="schedule" testTitle={testTitle} />
      )}

      <div className="container mx-auto px-4 py-6 max-w-6xl">
        <TestSchedulingStep
        testTitle={testTitle}
        onBack={handleBack}
        onScheduleAndCreate={handleScheduleAndCreate}
        onCreateWithoutScheduling={handleCreateWithoutScheduling}
        loading={loading}
      />
      </div>
    </div>
  );
};

export default TestSchedulePage;
